import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Calendar, Tag, ChevronRight, ChevronLeft, Newspaper, GraduationCap, Megaphone, X } from 'lucide-react';
import api from '../api/axios';

const CATEGORIES = [
  { key: '', label: 'Tümü', icon: Newspaper },
  { key: 'haber', label: 'Haberler', icon: Megaphone },
  { key: 'egitim', label: 'Eğitim Faaliyetleri', icon: GraduationCap },
  { key: 'duyuru', label: 'Duyurular', icon: Tag },
];

const ImageSlider = ({ images, height = 260 }) => {
  const [idx, setIdx] = React.useState(0);
  if (!images || images.length === 0) return null;
  if (images.length === 1) return (
    <div style={{ height, overflow: 'hidden' }}>
      <img src={images[0].image_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
    </div>
  );
  return (
    <div style={{ position: 'relative', height, overflow: 'hidden' }}>
      <img src={images[idx].image_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', transition: 'opacity 0.3s' }} />
      <button onClick={e => { e.stopPropagation(); setIdx(i => (i - 1 + images.length) % images.length); }}
        style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', background: 'rgba(0,0,0,0.4)', border: 'none', borderRadius: '50%', width: 32, height: 32, cursor: 'pointer', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <ChevronLeft size={16} />
      </button>
      <button onClick={e => { e.stopPropagation(); setIdx(i => (i + 1) % images.length); }}
        style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', background: 'rgba(0,0,0,0.4)', border: 'none', borderRadius: '50%', width: 32, height: 32, cursor: 'pointer', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <ChevronRight size={16} />
      </button>
      <div style={{ position: 'absolute', bottom: 10, left: 0, right: 0, display: 'flex', justifyContent: 'center', gap: 6 }}>
        {images.map((_, i) => (
          <button key={i} onClick={e => { e.stopPropagation(); setIdx(i); }}
            style={{ width: i === idx ? 20 : 8, height: 8, borderRadius: 4, background: i === idx ? 'white' : 'rgba(255,255,255,0.5)', border: 'none', cursor: 'pointer', transition: 'all 0.3s', padding: 0 }} />
        ))}
      </div>
    </div>
  );
};

const CategoryBadge = ({ category }) => {
  const map = {
    haber: { label: 'Haber', color: '#E30613', bg: 'rgba(227,6,19,0.1)' },
    egitim: { label: 'Eğitim', color: '#34c759', bg: 'rgba(52,199,89,0.1)' },
    duyuru: { label: 'Duyuru', color: '#ff9500', bg: 'rgba(255,149,0,0.1)' },
  };
  const cat = map[category] || { label: category, color: '#6b7280', bg: 'rgba(0,0,0,0.05)' };
  return (
    <span style={{ background: cat.bg, color: cat.color, borderRadius: 20, padding: '3px 10px', fontSize: 11, fontWeight: 700 }}>
      {cat.label}
    </span>
  );
};

const Modal = ({ item, onClose }) => {
  if (!item) return null;
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(8px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 }}>
      <div style={{ background: 'white', borderRadius: 20, width: '100%', maxWidth: 680, maxHeight: '90vh', overflow: 'hidden', boxShadow: '0 32px 80px rgba(0,0,0,0.25)', display: 'flex', flexDirection: 'column' }}>
        {item.images && item.images.length > 0 && (
          <div style={{ flexShrink: 0 }}>
            <ImageSlider images={item.images} height={260} />
          </div>
        )}
        <div style={{ padding: '24px 28px', flex: 1, overflowY: 'auto' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
            <div>
              <CategoryBadge category={item.category} />
              <h2 style={{ fontWeight: 800, fontSize: 22, color: '#111111', marginTop: 10, lineHeight: 1.3 }}>{item.title}</h2>
            </div>
            <button onClick={onClose} style={{ background: 'rgba(0,0,0,0.06)', border: 'none', borderRadius: '50%', width: 36, height: 36, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginLeft: 12 }}>
              <X size={16} />
            </button>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#9ca3af', fontSize: 12, marginBottom: 20 }}>
            <Calendar size={13} />
            {new Date(item.created_at).toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', year: 'numeric' })}
            {item.author_name && <span>· {item.author_name}</span>}
          </div>
          <p style={{ fontSize: 15, color: '#444444', lineHeight: 1.9, whiteSpace: 'pre-wrap' }}>{item.content}</p>
        </div>
      </div>
    </div>
  );
};

export default function Announcements() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCat, setSelectedCat] = useState('');
  const [selectedItem, setSelectedItem] = useState(null);

  useEffect(() => {
    const params = selectedCat ? `?category=${selectedCat}` : '';
    api.get('/announcements' + params).then(r => { setItems(r.data); setLoading(false); });
  }, [selectedCat]);

  const featured = items[0];
  const rest = items.slice(1);

  return (
    <div style={{ minHeight: '100vh', background: 'linear-gradient(135deg, #f0f2f5 0%, #e8eaf6 100%)' }}>
      {/* Header */}
      <div style={{ background: 'rgba(255,255,255,0.9)', backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)', borderBottom: '1px solid rgba(0,0,0,0.07)', padding: '0 24px', height: 58, display: 'flex', alignItems: 'center', gap: 16, position: 'sticky', top: 0, zIndex: 100 }}>
        <button onClick={() => navigate('/panel')} style={{ background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, color: '#E30613', fontWeight: 600, fontSize: 13 }}>
          <ArrowLeft size={16} /> Geri
        </button>
        <div style={{ width: 1, height: 20, background: 'rgba(0,0,0,0.1)' }} />
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Newspaper size={18} color="#E30613" />
          <span style={{ fontWeight: 700, fontSize: 16, color: '#111111' }}>Eğitim & Faaliyetler</span>
        </div>
      </div>

      <div style={{ maxWidth: 1100, margin: '0 auto', padding: '24px 16px' }}>

        {/* Kategori filtreleri */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 24, overflowX: 'auto', paddingBottom: 4 }}>
          {CATEGORIES.map(({ key, label, icon: Icon }) => (
            <button key={key} onClick={() => setSelectedCat(key)}
              style={{ padding: '8px 18px', borderRadius: 24, border: 'none', cursor: 'pointer', fontWeight: 600, fontSize: 13, flexShrink: 0, display: 'flex', alignItems: 'center', gap: 6, transition: 'all 0.2s', background: selectedCat === key ? '#E30613' : 'rgba(255,255,255,0.85)', color: selectedCat === key ? 'white' : '#6b7280', boxShadow: selectedCat === key ? '0 4px 14px rgba(227,6,19,0.25)' : '0 2px 6px rgba(0,0,0,0.06)' }}>
              <Icon size={14} /> {label}
            </button>
          ))}
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', padding: 60, color: '#9ca3af' }}>Yükleniyor...</div>
        ) : items.length === 0 ? (
          <div style={{ background: 'rgba(255,255,255,0.8)', borderRadius: 20, padding: 60, textAlign: 'center' }}>
            <Newspaper size={48} color="#d1d5db" strokeWidth={1} style={{ margin: '0 auto 16px' }} />
            <div style={{ fontWeight: 700, fontSize: 16, color: '#374151', marginBottom: 8 }}>İçerik bulunamadı</div>
            <div style={{ color: '#9ca3af', fontSize: 14 }}>Henüz yayınlanmış içerik yok.</div>
          </div>
        ) : (
          <>
            {/* Öne çıkan haber */}
            {featured && !selectedCat && (
              <div onClick={() => setSelectedItem(featured)}
                style={{ background: 'white', borderRadius: 20, overflow: 'hidden', marginBottom: 24, cursor: 'pointer', boxShadow: '0 4px 20px rgba(0,0,0,0.08)', transition: 'all 0.2s', border: '1px solid rgba(255,255,255,0.9)' }}
                onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-3px)'; e.currentTarget.style.boxShadow = '0 12px 36px rgba(0,0,0,0.12)'; }}
                onMouseLeave={e => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = '0 4px 20px rgba(0,0,0,0.08)'; }}>
                <div style={{ display: 'flex', flexDirection: window.innerWidth < 768 ? 'column' : 'row' }}>
                  {featured.images && featured.images.length > 0 && (
                    <div style={{ width: window.innerWidth < 768 ? '100%' : 420, height: window.innerWidth < 768 ? 220 : 280, flexShrink: 0, overflow: 'hidden' }}>
                      <ImageSlider images={featured.images} height={window.innerWidth < 768 ? 220 : 280} />
                    </div>
                  )}
                  <div style={{ padding: '28px 32px', flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
                      <span style={{ background: '#E30613', color: 'white', borderRadius: 6, padding: '3px 10px', fontSize: 11, fontWeight: 700 }}>ÖNE ÇIKAN</span>
                      <CategoryBadge category={featured.category} />
                    </div>
                    <h2 style={{ fontWeight: 800, fontSize: 22, color: '#111111', marginBottom: 12, lineHeight: 1.35 }}>{featured.title}</h2>
                    <p style={{ fontSize: 14, color: '#6b7280', lineHeight: 1.7, marginBottom: 18, display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{featured.content}</p>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#9ca3af', fontSize: 12 }}>
                        <Calendar size={13} />
                        {new Date(featured.created_at).toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', year: 'numeric' })}
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 4, color: '#E30613', fontWeight: 600, fontSize: 13 }}>
                        Devamını Oku <ChevronRight size={16} />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Kart grid */}
            <div style={{ display: 'grid', gridTemplateColumns: window.innerWidth < 768 ? '1fr' : 'repeat(auto-fill, minmax(320px, 1fr))', gap: 16 }}>
              {(selectedCat ? items : rest).map(item => (
                <div key={item.id} onClick={() => setSelectedItem(item)}
                  style={{ background: 'white', borderRadius: 16, overflow: 'hidden', cursor: 'pointer', boxShadow: '0 2px 12px rgba(0,0,0,0.07)', transition: 'all 0.2s', border: '1px solid rgba(255,255,255,0.9)', display: 'flex', flexDirection: 'column' }}
                  onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-3px)'; e.currentTarget.style.boxShadow = '0 10px 28px rgba(0,0,0,0.12)'; }}
                  onMouseLeave={e => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = '0 2px 12px rgba(0,0,0,0.07)'; }}>
                  {item.images && item.images.length > 0 && (
                    <div style={{ height: 180, overflow: 'hidden' }}>
                      <ImageSlider images={item.images} height={180} />
                    </div>
                  )}
                  {!item.image_url && (
                    <div style={{ height: 100, background: 'linear-gradient(135deg, #f0f2f5, #e8eaf6)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Newspaper size={36} color="#d1d5db" strokeWidth={1} />
                    </div>
                  )}
                  <div style={{ padding: '18px 20px', flex: 1, display: 'flex', flexDirection: 'column' }}>
                    <div style={{ marginBottom: 10 }}>
                      <CategoryBadge category={item.category} />
                    </div>
                    <h3 style={{ fontWeight: 700, fontSize: 15, color: '#111111', marginBottom: 8, lineHeight: 1.4 }}>{item.title}</h3>
                    <p style={{ fontSize: 13, color: '#6b7280', lineHeight: 1.7, flex: 1, display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden', marginBottom: 14 }}>{item.content}</p>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderTop: '1px solid #f5f5f5', paddingTop: 12 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 5, color: '#9ca3af', fontSize: 11 }}>
                        <Calendar size={11} />
                        {new Date(item.created_at).toLocaleDateString('tr-TR', { day: 'numeric', month: 'short', year: 'numeric' })}
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 3, color: '#E30613', fontSize: 12, fontWeight: 600 }}>
                        Oku <ChevronRight size={14} />
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      {selectedItem && <Modal item={selectedItem} onClose={() => setSelectedItem(null)} />}
    </div>
  );
}
